import { useCallback, useMemo, useRef, useState } from "react";
import { ReportType } from "~/feature/cart/cart/components/CartPage/Buttons/DownloadReportButton/api/downloadCartReport";
import { DownloadClickLink } from "../../common/components/Link";
import { PageTitle } from "~/common/components/PageTitle/v2/PageTitle";
import Spinner from "../../common/components/Spinner/Spinner";
import { toMapFn } from "../../utils/reducers";
import { QuoteListTable } from "./components/QuoteListTable/QuoteListTable";
import { IncludesSurchargeDisclaimer } from "../../common/components/Messages/IncludesSurchargeDisclaimer";
import { downloadQuoteHistoryReport, getReportLabels } from "./api/downloadQuoteHistoryReport";
import { type Subscription } from "rxjs";
import { getIsAllOpen, rowHasSurcharge } from "../OrderListPage/OrderListPage";
import {
  OrderListSearchBox,
  type OrderListSearchBoxData,
} from "../OrderListPage/components/OrderListSearchBox/OrderListSearchBox";
import classes from "../OrderListPage/OrderListPage.module.less";
import {
  filterProductsMO,
  type GetOrderListSearchResults,
  getQuoteListSearchResults,
  transformFormData,
} from "../common/DownloadOrderHistoryService/DownloadOrderHistoryService";
import { SearchBoxVariant } from "../OrderListPage/components/OrderListSearchBox/SearchBoxVariant";
import { fetchQuoteDetails } from "./api/fetchOrderDetails/fetchQuoteDetails";
import { useQuoteIsBundleInfoModal } from "./components/QuoteIsBundleInfoModal";
import { useTranslation } from "react-i18next";
import { transformQuoteDetails } from "~/OrdersQuotes/QuoteListPage/api/fetchOrderDetails/v2/transformQuoteDetails";
import { MultiAccountSearchDataProvider } from "~/OrdersQuotes/OrderListPage/components/OrderListSearchBox/MultiAccountSelect/MultiAccountSearchDataProvider";
import {
  canStartLoadingDetails,
  quoteListPageActions,
  type QuoteListRow,
  useQuoteListState,
} from "~/OrdersQuotes/QuoteListPage/useQuoteListState";
import { Button } from "~/common/components/Button/Button";
import { useQuoteListSelectionState } from "~/OrdersQuotes/common/components/v2/QuoteTable/useQuoteListSelectionState";
import { canQuoteListRowBeSelected, makeMapQuoteLineToATCAPIItem } from "~/OrdersQuotes/QuoteListPage/utils";
import type { AddToCartApiItem } from "~/feature/cart/cart/api/AddToCart";
import { sendDataMultipleQuotes } from "~/api/MultipleQuoteToCart";
import { addMessage } from "~/redux/globalMessages/actions";
import { useDispatch } from "react-redux";
import type { QuoteDetailsSelectionState } from "~/OrdersQuotes/common/components/v2/QuoteTable/useQuoteDetailsSelectionState";
import { openModal } from "~/AddToCart/ModalSevice";
import CartSuccessModal from "~/AddToCart/components/AddToCartProcess/Modals/CartSuccessModal/CartSuccessModal";
import { addBaseProductDataAction } from "~/redux/products/actions";
import { isErrorEntry } from "../common/utils/util";
import { Alert } from "~/common/m3/Alert/Alert";
import { ATCBar } from "../common/components/ATCBar";
import { getTotalPrice } from "~/feature/ProductDescriptor/v1/productDescriptorUtils";
import type { QuoteLineMO } from "~/OrdersQuotes/common/components/v2/models";

interface selectedATCItemsProps {
  selectedLineItems: number;
  selectedQuantity: number;
  totalPrice: PriceData;
}

const selectedATCItemsDefaultValue = {
  selectedLineItems: 0,
  selectedQuantity: 0,
  totalPrice: { value: 0, currencyIso: "" },
};

export const isFromDifferentAccOrSalesOrg = (row: QuoteListRow) => {
  return (
    (row.data.account?.code && row.data.account?.code !== window.SCHA.user.selectedAccount.uid) ||
    (row.data.account?.salesOrg && row.data.account?.salesOrg !== window.SCHA.user.selectedAccount.salesOrg)
  );
};

function hasRowAnythingSelected(row: QuoteListRow, selectionContext: Record<string, QuoteDetailsSelectionState>) {
  const documentState = selectionContext[row.data.soNumber];
  return documentState?.isAnythingSelected ?? false;
}

export const QuoteListPage = () => {
  const { t } = useTranslation();
  const [state, dispatch] = useQuoteListState();
  const reduxDispatch = useDispatch();
  const selectionContext = useQuoteListSelectionState();
  const { loading, error, results: rows } = state;
  const subscriptionRef = useRef<Subscription | null>(null);
  const submittedFormData = useRef<GetOrderListSearchResults | null>(null);

  const isAllOpen = getIsAllOpen(state.results);
  const webservicesDisabled = window.SCHA.config.disableAllWS;
  const [isATCInProgress, setIsATCInProgress] = useState(false);

  const selectedATCItems = useMemo(
    () => getSelectedATCItems(rows, selectionContext.selectionPerDocument),
    [rows, selectionContext],
  );

  const { requestConfirmation } = useQuoteIsBundleInfoModal();

  const onSubmitHandler = useCallback((formData: OrderListSearchBoxData) => {
    subscriptionRef.current?.unsubscribe();
    const transformedData = transformFormData(formData);
    submittedFormData.current = transformedData;
    dispatch(quoteListPageActions.beginLoading());
    subscriptionRef.current = getQuoteListSearchResults(transformedData).subscribe({
      error: () => dispatch(quoteListPageActions.errorLoading()),
      next: (data) => dispatch(quoteListPageActions.addResultPage(data)),
      complete: () => dispatch(quoteListPageActions.finishLoading()),
    });
  }, []);

  const loadSingleDetail = useCallback(async (row: QuoteListRow, preselect: boolean) => {
    const soNumber = row.data.soNumber;
    let accountNumber, salesOrg;
    if (isFromDifferentAccOrSalesOrg(row)) {
      accountNumber = row.data.account?.code;
      salesOrg = row.data.account?.salesOrg;
    }
    try {
      const dto = await fetchQuoteDetails(soNumber, accountNumber, salesOrg);
      const data = transformQuoteDetails(dto);
      reduxDispatch(addBaseProductDataAction(data.quoteLines.map((line) => line.product)));
      data.quoteLines = filterProductsMO(data.quoteLines, submittedFormData.current);
      selectionContext.init(data, preselect);
      dispatch(quoteListPageActions.finishLoadingDetails(data));
    } catch {
      dispatch(quoteListPageActions.errorLoadingDetails(soNumber));
    }
  }, []);

  const toggleOpenCb = useCallback(
    (row: QuoteListRow) => {
      if (canStartLoadingDetails(row)) {
        loadSingleDetail(row, false);
      }
      dispatch(quoteListPageActions.toggleOpen(row.data.soNumber));
    },
    [loadSingleDetail],
  );

  const toggleOpenAllCb = useCallback(() => {
    rows.forEach((row) => {
      const newState = !isAllOpen;
      if (newState && canStartLoadingDetails(row) && !isErrorEntry(row)) {
        // TODO - fetch service?
        loadSingleDetail(row, false);
      }
    });
    dispatch(quoteListPageActions.toggleAllOpen());
  }, [isAllOpen, loadSingleDetail, rows]);

  const toggleSelectAllQuotes = useCallback(() => {
    rows.forEach((row) => {
      if (!canQuoteListRowBeSelected(row)) {
        return;
      }
      if (canStartLoadingDetails(row)) {
        loadSingleDetail(row, true);
        if (!row.open) {
          dispatch(quoteListPageActions.toggleOpen(row.data.soNumber));
        }
      } else {
        selectionContext.toggleAllLines(row.data.soNumber);
      }
    });
  }, [rows, loadSingleDetail, selectionContext.toggleAllLines]);

  const toggleSelectLineItem = useCallback(
    async (soNumber: string, quoteLineNo: string) => {
      const quote = rows.find((item) => item.data.soNumber === soNumber)!;
      if (quote.data.isBundled) {
        const confirmation = await requestConfirmation();
        if (confirmation) {
          selectionContext.toggleAllLines(soNumber);
        }
      } else {
        selectionContext.toggleLine(soNumber, quoteLineNo);
      }
    },
    [rows, selectionContext.toggleAllLines, selectionContext.toggleLine],
  );

  const toggleSelectQuote = useCallback(
    (row: QuoteListRow) => {
      if (canStartLoadingDetails(row)) {
        loadSingleDetail(row, true);
        if (!row.open) {
          dispatch(quoteListPageActions.toggleOpen(row.data.soNumber));
        }
        return;
      } else {
        selectionContext.toggleAllLines(row.data.soNumber);
      }
    },
    [loadSingleDetail, selectionContext.toggleAllLines],
  );

  const downloadQuoteList = (fileType: ReportType) => {
    const reportLabels = toMapFn(
      getReportLabels(),
      (key) => key,
      (key) => t(key),
    );
    downloadQuoteHistoryReport(rows, fileType, reportLabels);
  };

  const disableCheckoutBtn = !rows.some((row) => hasRowAnythingSelected(row, selectionContext.selectionPerDocument));

  const containsSurcharge = rows.some(rowHasSurcharge);

  const disableInteractions = loading || error || webservicesDisabled;
  const showRows = !error && rows.length > 0;

  const renderReportButton = (type: ReportType, dataTestIdStr: string, typeStr: string) => (
    <DownloadClickLink
      onClick={() => downloadQuoteList(type)}
      data-test-id={`orderList-seatchBox-btn-${dataTestIdStr}`}
      disabled={disableInteractions}
    >
      {t(`downloadFile.type.${typeStr}`)}
    </DownloadClickLink>
  );

  const addQuotesToCart = async () => {
    const mapLineItem = makeMapQuoteLineToATCAPIItem();
    const quotes: { quoteNumber: string; items: AddToCartApiItem[] }[] = [];
    rows
      .filter((row) => {
        const documentSelectionState = selectionContext.selectionPerDocument[row.data.soNumber];
        return documentSelectionState?.isAnythingSelected ?? false;
      })
      .forEach((row) => {
        const documentSelectionState = selectionContext.selectionPerDocument[row.data.soNumber];
        const selectedLineItemMap = documentSelectionState?.selectedLineNos ?? {};
        const items = row.details!.quoteLines.filter((line) => selectedLineItemMap[line.lineNo]).flatMap(mapLineItem);
        quotes.push({ quoteNumber: row.data.soNumber, items });
      });

    const getAllItems = (
      quotes: {
        quoteNumber: string;
        items: AddToCartApiItem[];
      }[],
    ) => {
      const allItems = quotes.reduce((accumulator, quote) => accumulator.concat(quote.items), [] as AddToCartApiItem[]);
      return allItems;
    };

    setIsATCInProgress(true);

    if (quotes.length > 0) {
      try {
        const response = await sendDataMultipleQuotes(quotes);
        if (response.ok) {
          openModal("add-to-cart-dialog", (close) => (
            <CartSuccessModal formData={getAllItems(quotes)} onClose={close} page="QUOTE_LIST" />
          ));
          //reduxDispatch(addMessage("info", t("page.quoteDetails.addToCart.success")));
        } else {
          reduxDispatch(addMessage("error", t("page.quoteDetails.errorSending")));
        }
      } catch {
        reduxDispatch(addMessage("error", t("page.quoteDetails.errorSending")));
      } finally {
        setIsATCInProgress(false);
      }
    }
  };

  return (
    <div className="scha-order-history-page">
      <PageTitle>{t("quoteListPage.title")}</PageTitle>

      {loading && <Spinner className="mb-3" />}

      {showRows && (
        <h2 className={classes.total} data-test-id={"quotesAmount"}>
          {t("quoteListPage.quotesAmount", { count: rows?.length ?? 0 })}
        </h2>
      )}

      <OrderListSearchBox onSubmit={onSubmitHandler} variant={SearchBoxVariant.QUOTE} />

      <div className="d-flex flex-justify-end mb-30">
        <Button
          className={"ml-4"}
          data-test-id={"add-all-to-cart"}
          disabled={disableCheckoutBtn || disableInteractions}
          onClick={addQuotesToCart}
        >
          {t("quoteDetailsPage.checkout")}
        </Button>
      </div>

      {!loading && rows.length > 0 && (
        <div className={classes.reportButtonContainer}>
          <span className="scha__order-list__download-title">{t("quoteListPage.downloads.title")}</span>
          {renderReportButton(ReportType.CSV, "downloadCSV", "csv")}
          {renderReportButton(ReportType.XLSX, "downloadExcel", "excel")}
        </div>
      )}

      {showRows && (
        <QuoteListTable
          rows={rows}
          toggleOpen={toggleOpenCb}
          toggleOpenAll={toggleOpenAllCb}
          toggleSelectLineItem={toggleSelectLineItem}
          toggleSelectQuote={toggleSelectQuote}
          toggleSelectAllQuotes={toggleSelectAllQuotes}
          selectionPerDocument={selectionContext.selectionPerDocument}

          // toggleSelect={toggleSelect}
          // toggleSelectLineItem={toggleSelectLineItem}
          // toggleSelectAll={toggleSelectAll}
        />
      )}

      {<IncludesSurchargeDisclaimer hasSurcharge={containsSurcharge} className={"mt-30"} />}

      {!webservicesDisabled && !loading && error && <Alert severity="error">{t("quoteListPage.error")}</Alert>}

      {!loading && !error && !rows.length && (
        <h2 className="scha__order-history__total my-0">{t("quoteListPage.noResults")}</h2>
      )}

      {selectedATCItems.selectedLineItems > 0 && (
        <ATCBar
          selectedLineItems={selectedATCItems.selectedLineItems}
          selectedQuantity={selectedATCItems.selectedQuantity}
          totalPrice={selectedATCItems.totalPrice}
          onAddToCart={addQuotesToCart}
          loading={isATCInProgress}
        />
      )}
    </div>
  );
};

export function createQuoteListPage(data: { accounts: string; filters: string }) {
  return (
    <MultiAccountSearchDataProvider accountsJson={data.accounts}>
      <QuoteListPage />
    </MultiAccountSearchDataProvider>
  );
}

const getSelectedATCItems = (
  rows: QuoteListRow[],
  selectionPerDocument: Record<string, QuoteDetailsSelectionState>,
): selectedATCItemsProps => {
  const selectedQuoteLineItems: QuoteLineMO[] = rows.flatMap((row) => {
    const documentSelectionState = selectionPerDocument[row.data.soNumber];
    if (!documentSelectionState?.isAnythingSelected) {
      return [];
    }

    const selectedLineItemMap = documentSelectionState.selectedLineNos ?? {};
    return row.details?.quoteLines.filter((line) => selectedLineItemMap[line.lineNo]) ?? [];
  });

  if (selectedQuoteLineItems.length === 0) {
    return selectedATCItemsDefaultValue;
  }

  const totalPieces = selectedQuoteLineItems.reduce(
    (total, item) => total + item.parts.reduce((sum, part) => sum + part.confirmedQuantity, 0),
    0,
  );

  const currencyIso = selectedQuoteLineItems[0]?.parts[0]?.price?.currencyIso;

  const totalPrice = selectedQuoteLineItems.reduce(
    (total, item) =>
      total +
      item.parts.reduce((sum, part) => {
        const priceData = getTotalPrice(part.price, part.confirmedQuantity);
        if (priceData.currencyIso !== currencyIso) {
          throw new Error("Mismatched currencies in selected items");
        }
        return sum + priceData.value;
      }, 0),
    0,
  );

  return {
    selectedLineItems: selectedQuoteLineItems.length,
    selectedQuantity: totalPieces,
    totalPrice: {
      currencyIso: currencyIso ?? "",
      value: totalPrice,
    },
  };
};
